import React, { useState, useEffect } from 'react';
import Avatar from './components/Avatar';
import Auth from './components/Auth';
import LandingPage from './components/LandingPage';
import ExpandableWindow from './components/ExpandableWindow';
import CategorySelection from './components/CategorySelection';
import XPNotification from './components/XPNotification';
import { Message, Lesson, Achievement, Category, BattlePassReward, ChatStyle } from './types';
import { 
  initialMessages, 
  mockLessons,
  mockBattlePassRewards, 
  mockUserStats,
  generateResponse,
  checkAndAwardAchievements
} from './data/mockData';
import { mockCategories } from './data/mockCategories';
import { v4 as uuidv4 } from 'uuid';
import { Search, Wifi, Volume2, Battery, Globe, FolderOpen, Mail, Images, CalendarDays } from 'lucide-react';
import { supabase } from './utils/supabaseClient';

function App() {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [isTyping, setIsTyping] = useState(false);
  const [userStats, setUserStats] = useState({ ...mockUserStats, selectedAvatar: 0 });
  const [activeTab, setActiveTab] = useState('chat');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState(mockCategories);
  const [battlePassRewards, setBattlePassRewards] = useState(mockBattlePassRewards);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [xpNotification, setXpNotification] = useState<number | null>(null);
  const [chatStyle, setChatStyle] = useState<ChatStyle>('cbt');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLanding, setShowLanding] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setAuthenticated(!!session);
    } catch (error) {
      console.error('Error checking auth status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const calculateXpToNextLevel = (currentLevel: number) => {
    return Math.round(500 * Math.pow(1.5, currentLevel - 1));
  };
  
  const showXPNotification = (amount: number) => {
    setXpNotification(null);
    setTimeout(() => setXpNotification(amount), 50);
  };
  
  const updateXP = (xpAmount: number) => {
    setUserStats(prev => {
      const newXP = prev.xp + xpAmount;
      let newLevel = prev.level;
      let remainingXP = newXP;
      let xpToNextLevel = prev.xpToNextLevel;

      while (remainingXP >= xpToNextLevel) {
        remainingXP -= xpToNextLevel;
        newLevel++;
        xpToNextLevel = calculateXpToNextLevel(newLevel);
      }

      return {
        ...prev,
        level: newLevel,
        xp: remainingXP,
        xpToNextLevel: xpToNextLevel
      };
    });

    showXPNotification(xpAmount);
  };

  const splitResponse = (response: string): string[] => {
    const segments = response.split(/\[第\d+段\]\s*/);
    return segments.filter(segment => segment.trim().length > 0);
  };
  
  const handleSendMessage = async (content: string) => {
    const userMessage: Message = {
      id: uuidv4(),
      content,
      sender: 'user',
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    
    updateXP(1);
    
    try {
      const aiResponse = await generateResponse(content, chatStyle);
      const segments = splitResponse(aiResponse);

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const aiMessage: Message = {
          id: uuidv4(),
          content: segment.trim(),
          sender: 'ai',
          timestamp: new Date(),
        };
        
        await new Promise(resolve => setTimeout(resolve, 500));
        setMessages(prev => [...prev, aiMessage]);
      }

      checkAndAwardAchievements('chat');
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage: Message = {
        id: uuidv4(),
        content: "對唔住，我而家處理唔到你嘅請求。等陣再試過啦。",
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };
  
  const handleSelectLesson = (lesson: Lesson, earnedXp: number) => {
    const updatedCategories = categories.map(category => ({
      ...category,
      lessons: category.lessons.map(l => 
        l.id === lesson.id 
          ? { ...l, completed: true, stars: 3 }
          : l
      )
    }));

    setCategories(updatedCategories);
    
    if (selectedCategory) {
      setSelectedCategory({
        ...selectedCategory,
        lessons: selectedCategory.lessons.map(l =>
          l.id === lesson.id
            ? { ...l, completed: true, stars: 3 }
            : l
        )
      });
    }
    
    if (!lesson.completed) {
      updateXP(earnedXp);
      checkAndAwardAchievements('lesson');
    }
    
    const aiResponse: Message = {
      id: uuidv4(),
      content: `Congratulations on completing "${lesson.title}"! You've earned ${earnedXp} XP.`,
      sender: 'ai',
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, aiResponse]);
    setActiveTab('chat');
  };

  const handleClaimReward = (rewardId: string) => {
    setBattlePassRewards(prev => 
      prev.map(reward => 
        reward.id === rewardId ? { ...reward, claimed: true } : reward
      )
    );

    const reward = battlePassRewards.find(r => r.id === rewardId);
    if (reward?.type === 'coins' && reward.value) {
      setUserStats(prev => ({
        ...prev,
        coins: prev.coins + reward.value!
      }));
    }
  };

  const handleUpdateAvatar = (index: number) => {
    setUserStats(prev => ({
      ...prev,
      selectedAvatar: index
    }));
  };

  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-black">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (showLanding && !authenticated) {
    return <LandingPage onGetStarted={() => setShowLanding(false)} />;
  }

  if (!authenticated) {
    return <Auth onAuth={() => setAuthenticated(true)} />;
  }

  return (
    <div className="fixed inset-0 flex flex-col">
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at center, #1e3a8a 0%, #000 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {xpNotification !== null && (
        <XPNotification
          xp={xpNotification}
          onClose={() => setXpNotification(null)}
        />
      )}

      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 50% 50%, #3b82f6 0%, transparent 60%)',
          filter: 'blur(40px)',
          transform: 'scale(1.2)'
        }}
      />

      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(45deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.2) 100%)',
          mixBlendMode: 'overlay'
        }}
      />

      <div className="relative flex-1">
        <ExpandableWindow 
          isExpanded={expanded}
          messages={messages}
          selectedCategory={selectedCategory}
          categories={categories}
          rewards={battlePassRewards}
          userStats={userStats}
          onSendMessage={handleSendMessage}
          onSelectLesson={handleSelectLesson}
          onSelectCategory={handleSelectCategory}
          onBackToCategories={handleBackToCategories}
          isTyping={isTyping}
          onClaimReward={handleClaimReward}
          onUpdateAvatar={handleUpdateAvatar}
          chatStyle={chatStyle}
          onChangeChatStyle={setChatStyle}
        />
      </div>

      <div className="fixed bottom-16 right-6 z-[9999]">
        <Avatar 
          expanded={expanded} 
          toggleExpand={toggleExpand} 
          lastMessage={messages.length > 0 ? messages[messages.length - 1].content : ''}
          isTyping={isTyping}
          selectedAvatar={userStats.selectedAvatar}
        />
      </div>

      <div className="relative h-12 bg-black bg-opacity-20 backdrop-blur-lg border-t border-white border-opacity-10 px-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button className="w-8 h-8 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors flex items-center justify-center">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/8/87/Windows_logo_-_2021.svg"
              alt="Windows"
              className="w-4 h-4"
            />
          </button>
          <div className="flex items-center space-x-2 px-3 py-1.5 rounded-md bg-white bg-opacity-5 hover:bg-opacity-10 transition-colors">
            <Search className="w-4 h-4 text-white opacity-70" />
            <span className="text-sm text-white opacity-70">Search</span>
          </div>
          
          <div className="flex items-center space-x-1 ml-2">
            <button className="w-8 h-8 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors flex items-center justify-center">
              <Globe className="w-5 h-5 text-white opacity-70" />
            </button>
            <button className="w-8 h-8 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-white opacity-70" />
            </button>
            <button className="w-8 h-8 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors flex items-center justify-center">
              <Mail className="w-5 h-5 text-white opacity-70" />
            </button>
            <button className="w-8 h-8 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors flex items-center justify-center">
              <Images className="w-5 h-5 text-white opacity-70" />
            </button>
            <button className="w-8 h-8 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-white opacity-70" />
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <Wifi className="w-4 h-4 text-white opacity-70" />
            <Volume2 className="w-4 h-4 text-white opacity-70" />
            <Battery className="w-4 h-4 text-white opacity-70" />
          </div>
          <div className="text-white opacity-70 text-sm">
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;